import React, { useEffect, useState } from 'react';
import { createGesture, IonContent, IonFooter, IonPage } from '@ionic/react';
import { Link } from 'react-router-dom';
import { AppLauncher } from '@capacitor/app-launcher';
import Nav from './Nav';
import { sidnavStore, SidenavState } from '../api/SidenavStore';
import { ApiService } from '../api/ApiService';
import { UserState, userStore } from '../api/AuthService';
import { UserUtils } from '../utils/UserUtils';
import TripFirstIn from '../components/walkthrough/TripFirstIn';
import HeroChrome from '../components/HeroChrome';
import { FolderDocIcon, ScanCornersIcon } from '../components/icons';
import {
  LicensePlateSvg,
  SubmitPaperSvg,
  TarpPhotoSvg,
  TrailerSvg,
} from '../components/svgIcons';
import { config } from '../Constants';
import { useLogPageVisit } from '../utils/logPageVisit';
import { logUserAction } from '../utils/logUserAction';
import { runStatus } from '../components/walkthrough/FirstSignIn';

function useTripData() {
  useLogPageVisit('LP Trip', 'User viewed the Trip page');
  const { driver } = userStore((state: UserState) => ({
    driver: state.driver,
  }));

  const [mounted, setMounted] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    ApiService.chatConversationGet('trip', {})
      .then((it: any) => {
        if (!Array.isArray(it)) return;
        let count = 0;
        for (let i = 0; i < it.length; i++) {
          if (it[i]?.lastMessage?.read !== 1) count += 1;
        }
        setMsgCount(count);
      })
      .catch(() => {});

    if (!UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector()) {
      ApiService.getPushNotifications('trip')
        .then((it: any) => {
          if (!Array.isArray(it)) return;
          setNotificationCount(it.filter((notification: any) => !notification.read).length);
        })
        .catch(() => {});
    }
  }, []);

  return {
    mounted,
    msgCount,
    notificationCount,
    unit: driver?.unit ? String(driver.unit) : '—',
  };
}

const transfloClick = () => {
  logUserAction('LP Tranflo Route', 'User has been routed to the Transflo app');
  const deepLink = `transflomobileplus://scan?recipientid=WSXI&sourceapp=westerndrivermobile&doctypes=TOD,BOL,TRP&driverid=${localStorage
    .getItem('username')
    ?.toUpperCase()}&truckid=${userStore.getState().driver.unit}`;
  AppLauncher.openUrl({ url: deepLink });
};

const Trip = () => {
  const { close }: { close: () => void } = sidnavStore((state: SidenavState) => ({
    open: state.open,
    close: state.close,
  }));
  const { toggleSidenav }: { toggleSidenav: () => void } = sidnavStore((state: SidenavState) => ({
    toggleSidenav: state.toggle,
  }));

  const ref = React.useRef<HTMLDivElement>(null);
  const tripData = useTripData();

  useEffect(() => {
    if (config.showSidenav) gestureInit();
  }, []);

  const gestureInit = () => {
    const card = ref.current;
    if (!card) return;
    const gesture = createGesture({
      el: card,
      gestureName: 'swipeMenu',
      onMove: () => {},
      onEnd: () => {
        runStatus ? console.log('locked') : toggleSidenav();
      },
    });
    gesture.enable();
  };

  return (
    <TripFirstIn>
      <IonPage>
        <IonContent fullscreen className="design-a-content">
          <div
            ref={ref}
            className={`design-a${tripData.mounted ? ' design-a--mounted' : ''}`}
            onClick={() => close()}
          >
            <div className="home-hero">
              <HeroChrome
                onMenuClick={() => toggleSidenav()}
                msgCount={tripData.msgCount}
                notificationCount={tripData.notificationCount}
              />

              <div className="trip-hero">
                <div className="trip-hero__badge">
                  <FolderDocIcon color="#ffffff" />
                </div>
                <div className="trip-hero__title home-display">Trip Documentation</div>
                <div className="trip-hero__subtitle">Photos &amp; paperwork · Unit {tripData.unit}</div>
              </div>
            </div>

            <div className="home-sheet">
              <div className="trip-sections">
                <section className="trip-section">
                  <div className="home-section-label">Per Trip</div>
                  <div className="trip-section-grid">
                    <Link to="/trailertracking" className="trip-tile trip-tile--1" id="trip-trailer-tracking">
                      <div className="trip-tile__icon">
                        <TrailerSvg />
                      </div>
                      <div className="trip-tile__text">
                        <div className="trip-tile__title">Trailer Tracking</div>
                      </div>
                    </Link>

                    <Link to="/photoupload" className="trip-tile trip-tile--2" id="trip-tarp-photo">
                      <div className="trip-tile__icon">
                        <TarpPhotoSvg />
                      </div>
                      <div className="trip-tile__text">
                        <div className="trip-tile__title">Tarp Photo</div>
                      </div>
                    </Link>

                    <button
                      type="button"
                      className="trip-tile trip-tile--paperwork trip-tile--span2 trip-tile--3"
                      id="trip-submit-paperwork"
                      onClick={e => {
                        e.stopPropagation();
                        transfloClick();
                      }}
                    >
                      <div className="trip-tile__icon">
                        <SubmitPaperSvg />
                      </div>
                      <div className="trip-tile__text">
                        <div className="trip-tile__title">Submit Paperwork</div>
                        <div className="trip-tile__tag">Transflo</div>
                      </div>
                      <div className="trip-tile__scan" aria-hidden="true">
                        <ScanCornersIcon />
                      </div>
                    </button>
                  </div>
                </section>

                <section className="trip-section">
                  <div className="home-section-label">As Needed</div>
                  <div className="trip-section-grid">
                    <Link to="/leasepurchase" className="trip-tile trip-tile--4">
                      <div className="trip-tile__icon">
                        <FolderDocIcon size={22} />
                      </div>
                      <div className="trip-tile__text">
                        <div className="trip-tile__title">Vehicle Inspection</div>
                        <div className="trip-tile__tag">Lease Purchase Program Only</div>
                      </div>
                    </Link>

                    <Link to="/traileridphotos" className="trip-tile trip-tile--5" id="trip-trailer-plate">
                      <div className="trip-tile__icon">
                        <LicensePlateSvg />
                      </div>
                      <div className="trip-tile__text">
                        <div className="trip-tile__title">Trailer Plate Photos</div>
                      </div>
                    </Link>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </IonContent>
        <IonFooter>
          <Nav pageName="Trip" />
        </IonFooter>
      </IonPage>
    </TripFirstIn>
  );
};

export default Trip;
